import React, { useState, useEffect } from 'react';
import { Building, Mail, Phone, MapPin, Star, CheckCircle, Ban, Plus, Edit, Trash2, Search, Award, Globe, Users, Bed, Calendar, Activity, X } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const SPECIALTIES_OPTIONS = [
  'Cardiology', 'Orthopedics', 'Neurology', 'Oncology', 'Gastroenterology',
  'Pulmonology', 'Nephrology', 'Urology', 'Ophthalmology', 'ENT',
  'Dermatology', 'Psychiatry', 'Pediatrics', 'Gynecology', 'Obstetrics',
  'General Surgery', 'Plastic Surgery', 'Vascular Surgery', 'Dental',
  'Physiotherapy', 'Radiology', 'Pathology', 'Emergency Medicine',
  'Fertility & IVF', 'Bariatric Surgery', 'Spine Surgery', 'Transplant',
  'Ayurveda', 'Homeopathy', 'Sports Medicine'
];

const TREATMENTS_OPTIONS = [
  'Heart Bypass Surgery', 'Angioplasty', 'Knee Replacement', 'Hip Replacement',
  'Spine Surgery', 'Brain Surgery', 'Cancer Treatment', 'Chemotherapy',
  'Radiation Therapy', 'Kidney Transplant', 'Liver Transplant', 'Heart Transplant',
  'IVF Treatment', 'Dental Implants', 'Cosmetic Surgery', 'Hair Transplant',
  'Bariatric Surgery', 'Eye Surgery', 'LASIK', 'Cataract Surgery'
];

const FACILITIES_OPTIONS = [
  '24/7 Emergency', 'ICU', 'NICU', 'Operation Theaters', 'Blood Bank',
  'Pharmacy', 'Diagnostic Lab', 'MRI', 'CT Scan', 'X-Ray', 'Ultrasound',
  'Ambulance Service', 'Air Ambulance', 'Cafeteria', 'Parking',
  'WiFi', 'International Patient Lounge', 'Visa Assistance', 
  'Airport Pickup', 'Interpreter Services', 'Private Rooms', 'VIP Suites'
];

const ACCREDITATION_OPTIONS = [
  'NABH', 'JCI', 'NABL', 'ISO 9001:2015', 'ISO 14001', 'QAI', 'AHPI'
];

const LANGUAGES_OPTIONS = [
  'English', 'Hindi', 'Arabic', 'Russian', 'French', 'German', 
  'Spanish', 'Mandarin', 'Japanese', 'Korean', 'Bengali', 'Tamil'
];

const INSURANCE_OPTIONS = [
  'Aetna', 'Cigna', 'Blue Cross', 'United Healthcare', 'Allianz',
  'BUPA', 'AXA', 'MetLife', 'Star Health', 'ICICI Lombard', 'HDFC Ergo',
  'Max Bupa', 'Religare', 'Apollo Munich', 'Bajaj Allianz'
];

export default function HospitalManagementFull() {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    email: '',
    phone: '',
    secondary_phone: '',
    website: '',
    
    // Location
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    google_maps_link: '',
    
    // Hospital Details
    established_year: '',
    hospital_type: 'multi-specialty',
    bed_count: '',
    icu_beds: '',
    operation_theaters: '',
    doctors_count: '',
    nurses_count: '',
    
    // Certifications & Accreditations
    accreditations: [],
    registration_number: '',
    
    // Medical Information
    specialties: [],
    treatments: [],
    facilities: [],
    
    // International Patient Services
    languages_spoken: [],
    insurance_accepted: [],
    visa_assistance: false,
    airport_pickup: false,
    interpreter_services: false,
    international_patient_coordinator: false,
    
    // Statistics
    success_rate: '',
    international_patients_yearly: '',
    surgeries_yearly: '',
    patient_satisfaction: '',
    
    // Description & Media
    description: '',
    short_description: '',
    logo_url: '',
    images: []
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });

  useEffect(() => {
    fetchHospitals();
  }, []);

  const fetchHospitals = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/hospitals-management`, {
        headers: { }
      });

      if (response.ok) {
        const data = await response.json();
        setHospitals(data.hospitals || []);
      }
    } catch (err) {
      toast.error('Failed to fetch hospitals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/hospitals-management`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Hospital created successfully');
        setShowCreateDialog(false);
        resetForm();
        fetchHospitals();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create hospital');
      }
    } catch (err) {
      toast.error('Failed to create hospital');
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/hospitals-management/${selectedHospital._id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Hospital updated successfully');
        setShowEditDialog(false);
        resetForm();
        fetchHospitals();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update hospital');
      }
    } catch (err) {
      toast.error('Failed to update hospital');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/hospitals-management/${selectedHospital._id}`, {
        method: 'DELETE',
        headers: { }
      });

      if (response.ok) {
        toast.success('Hospital deleted successfully');
        setShowDeleteDialog(false);
        setSelectedHospital(null);
        fetchHospitals();
      } else {
        toast.error('Failed to delete hospital');
      }
    } catch (err) {
      toast.error('Failed to delete hospital');
    }
  };

  const handleStatusChange = async (hospitalId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/hospitals-management/${hospitalId}/status`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Hospital ${newStatus} successfully`);
        fetchHospitals();
      }
    } catch (err) {
      toast.error('Failed to update hospital status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      secondary_phone: '',
      website: '',
      address: '',
      city: '',
      state: '',
      country: 'India',
      pincode: '',
      google_maps_link: '',
      established_year: '',
      hospital_type: 'multi-specialty',
      bed_count: '',
      icu_beds: '',
      operation_theaters: '',
      doctors_count: '',
      nurses_count: '',
      accreditations: [],
      registration_number: '',
      specialties: [],
      treatments: [],
      facilities: [],
      languages_spoken: [],
      insurance_accepted: [],
      visa_assistance: false,
      airport_pickup: false,
      interpreter_services: false,
      international_patient_coordinator: false,
      success_rate: '',
      international_patients_yearly: '',
      surgeries_yearly: '',
      patient_satisfaction: '',
      description: '',
      short_description: '',
      logo_url: '',
      images: []
    });
    setSelectedHospital(null);
    setActiveTab('basic');
  };

  const openEditDialog = (hospital) => {
    setSelectedHospital(hospital);
    setFormData({
      name: hospital.name || '',
      email: hospital.email || '',
      phone: hospital.phone || '',
      secondary_phone: hospital.secondary_phone || '',
      website: hospital.website || '',
      address: hospital.address || '',
      city: hospital.city || '',
      state: hospital.state || '',
      country: hospital.country || 'India',
      pincode: hospital.pincode || '',
      google_maps_link: hospital.google_maps_link || '',
      established_year: hospital.established_year || '',
      hospital_type: hospital.hospital_type || 'multi-specialty',
      bed_count: hospital.bed_count || '',
      icu_beds: hospital.icu_beds || '',
      operation_theaters: hospital.operation_theaters || '',
      doctors_count: hospital.doctors_count || '',
      nurses_count: hospital.nurses_count || '',
      accreditations: hospital.accreditations || [],
      registration_number: hospital.registration_number || '',
      specialties: hospital.specialties || [],
      treatments: hospital.treatments || [],
      facilities: hospital.facilities || [],
      languages_spoken: hospital.languages_spoken || [],
      insurance_accepted: hospital.insurance_accepted || [],
      visa_assistance: hospital.visa_assistance || false,
      airport_pickup: hospital.airport_pickup || false,
      interpreter_services: hospital.interpreter_services || false,
      international_patient_coordinator: hospital.international_patient_coordinator || false,
      success_rate: hospital.success_rate || '',
      international_patients_yearly: hospital.international_patients_yearly || '',
      surgeries_yearly: hospital.surgeries_yearly || '',
      patient_satisfaction: hospital.patient_satisfaction || '',
      description: hospital.description || '',
      short_description: hospital.short_description || '',
      logo_url: hospital.logo_url || '',
      images: hospital.images || []
    });
    setShowEditDialog(true);
  };

  const toggleArrayItem = (field, item) => {
    const currentItems = formData[field] || [];
    if (currentItems.includes(item)) {
      setFormData({ ...formData, [field]: currentItems.filter(i => i !== item) });
    } else {
      setFormData({ ...formData, [field]: [...currentItems, item] });
    }
  };

  const filteredHospitals = hospitals.filter(hospital => {
    const matchesSearch = filters.search === '' || 
      hospital.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      hospital.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
      hospital.city?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = filters.status === 'all' || hospital.account_status === filters.status;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      verified: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      suspended: 'bg-red-100 text-red-800',
      rejected: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || styles.pending;
  };

  const stats = {
    total: hospitals.length,
    verified: hospitals.filter(h => h.account_status === 'verified').length,
    pending: hospitals.filter(h => h.account_status === 'pending').length,
    suspended: hospitals.filter(h => h.account_status === 'suspended').length
  };

  // Render the multi-select tags
  const renderTagSelector = (field, options, label) => (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
        <div className="flex flex-wrap gap-2">
          {options.map(option => (
            <Badge
              key={option}
              variant={formData[field]?.includes(option) ? 'default' : 'outline'}
              className={`cursor-pointer transition-colors ${
                formData[field]?.includes(option) 
                  ? 'bg-orange-600 text-white hover:bg-orange-700' 
                  : 'hover:bg-orange-50'
              }`}
              onClick={() => toggleArrayItem(field, option)}
            >
              {option}
              {formData[field]?.includes(option) && (
                <X className="w-3 h-3 ml-1" />
              )}
            </Badge>
          ))}
        </div>
      </div>
      {formData[field]?.length > 0 && (
        <p className="text-xs text-gray-500 mt-1">{formData[field].length} selected</p>
      )}
    </div>
  );

  // Form content with tabs
  const renderFormContent = () => (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid w-full grid-cols-5 mb-4">
        <TabsTrigger value="basic">Basic</TabsTrigger>
        <TabsTrigger value="details">Details</TabsTrigger>
        <TabsTrigger value="medical">Medical</TabsTrigger>
        <TabsTrigger value="international">International</TabsTrigger>
        <TabsTrigger value="stats">Stats & Media</TabsTrigger>
      </TabsList>

      <TabsContent value="basic" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Hospital Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Apollo Hospital"
              data-testid="input-hospital-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="contact@hospital.com"
              data-testid="input-hospital-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Primary Phone *</label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+91 9876543210"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Secondary Phone</label>
            <Input
              value={formData.secondary_phone}
              onChange={(e) => setFormData({ ...formData, secondary_phone: e.target.value })}
              placeholder="+91 9876543211"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Website</label>
            <Input
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://hospital.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hospital Type</label>
            <Select value={formData.hospital_type} onValueChange={(v) => setFormData({ ...formData, hospital_type: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="multi-specialty">Multi-Specialty</SelectItem>
                <SelectItem value="super-specialty">Super-Specialty</SelectItem>
                <SelectItem value="single-specialty">Single-Specialty</SelectItem>
                <SelectItem value="clinic">Clinic</SelectItem>
                <SelectItem value="diagnostic-center">Diagnostic Center</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Address *</label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Full street address"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">City *</label>
            <Input
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              placeholder="Mumbai"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">State *</label>
            <Input
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              placeholder="Maharashtra"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Country</label>
            <Input
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              placeholder="India"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Pincode</label>
            <Input
              value={formData.pincode}
              onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
              placeholder="400001"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Google Maps Link</label>
            <Input
              value={formData.google_maps_link}
              onChange={(e) => setFormData({ ...formData, google_maps_link: e.target.value })}
              placeholder="https://goo.gl/maps/..."
            />
          </div>
        </div>
      </TabsContent>

      <TabsContent value="details" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Established Year</label>
            <Input
              type="number"
              value={formData.established_year}
              onChange={(e) => setFormData({ ...formData, established_year: e.target.value })}
              placeholder="1990"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Registration Number</label>
            <Input
              value={formData.registration_number}
              onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
              placeholder="MH/2020/12345"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Total Beds</label>
            <Input
              type="number"
              value={formData.bed_count}
              onChange={(e) => setFormData({ ...formData, bed_count: e.target.value })}
              placeholder="500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ICU Beds</label>
            <Input
              type="number"
              value={formData.icu_beds}
              onChange={(e) => setFormData({ ...formData, icu_beds: e.target.value })}
              placeholder="50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Operation Theaters</label>
            <Input
              type="number"
              value={formData.operation_theaters}
              onChange={(e) => setFormData({ ...formData, operation_theaters: e.target.value })}
              placeholder="15"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Doctors Count</label>
            <Input
              type="number"
              value={formData.doctors_count}
              onChange={(e) => setFormData({ ...formData, doctors_count: e.target.value })}
              placeholder="200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Nurses Count</label>
            <Input
              type="number"
              value={formData.nurses_count}
              onChange={(e) => setFormData({ ...formData, nurses_count: e.target.value })}
              placeholder="500"
            />
          </div>
        </div>

        <div className="mt-6">
          {renderTagSelector('accreditations', ACCREDITATION_OPTIONS, 'Accreditations & Certifications')}
        </div>

        <div className="mt-4">
          {renderTagSelector('facilities', FACILITIES_OPTIONS, 'Facilities & Amenities')}
        </div>
      </TabsContent>

      <TabsContent value="medical" className="space-y-4">
        {renderTagSelector('specialties', SPECIALTIES_OPTIONS, 'Medical Specialties *')}
        
        <div className="mt-4">
          {renderTagSelector('treatments', TREATMENTS_OPTIONS, 'Treatments Offered')}
        </div>
      </TabsContent>

      <TabsContent value="international" className="space-y-4">
        {renderTagSelector('languages_spoken', LANGUAGES_OPTIONS, 'Languages Spoken')}
        
        <div className="mt-4">
          {renderTagSelector('insurance_accepted', INSURANCE_OPTIONS, 'Insurance Accepted')}
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium mb-3">International Patient Services</label>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.visa_assistance}
                onChange={(e) => setFormData({ ...formData, visa_assistance: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm">Visa Assistance</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.airport_pickup}
                onChange={(e) => setFormData({ ...formData, airport_pickup: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm">Airport Pickup</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.interpreter_services}
                onChange={(e) => setFormData({ ...formData, interpreter_services: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm">Interpreter Services</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.international_patient_coordinator}
                onChange={(e) => setFormData({ ...formData, international_patient_coordinator: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm">International Patient Coordinator</span>
            </label>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="stats" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Success Rate (%)</label>
            <Input
              type="number"
              value={formData.success_rate}
              onChange={(e) => setFormData({ ...formData, success_rate: e.target.value })}
              placeholder="98"
              max="100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">International Patients/Year</label>
            <Input
              type="number"
              value={formData.international_patients_yearly}
              onChange={(e) => setFormData({ ...formData, international_patients_yearly: e.target.value })}
              placeholder="5000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Surgeries/Year</label>
            <Input
              type="number"
              value={formData.surgeries_yearly}
              onChange={(e) => setFormData({ ...formData, surgeries_yearly: e.target.value })}
              placeholder="10000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Patient Satisfaction (%)</label>
            <Input
              type="number"
              value={formData.patient_satisfaction}
              onChange={(e) => setFormData({ ...formData, patient_satisfaction: e.target.value })}
              placeholder="95"
              max="100"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Short Description</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            rows="2"
            value={formData.short_description}
            onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
            placeholder="Brief tagline about the hospital (max 150 chars)"
            maxLength={150}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Full Description</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            rows="4"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Detailed description about the hospital, its history, achievements..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Logo URL</label>
          <Input
            value={formData.logo_url}
            onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
            placeholder="https://example.com/logo.png"
          />
        </div>
      </TabsContent>
    </Tabs>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading hospitals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hospital-management-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Hospital Management</h1>
          <p className="text-gray-600 mt-1">Manage hospital partners and facilities</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="add-hospital-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Hospital
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Hospitals</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Building className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Verified</p>
                <p className="text-2xl font-bold text-green-600">{stats.verified}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <Award className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Suspended</p>
                <p className="text-2xl font-bold text-red-600">{stats.suspended}</p>
              </div>
              <Ban className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or city..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                  data-testid="search-hospitals"
                />
              </div>
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Hospitals List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="hospitals-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Hospital</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Beds</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Specialties</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredHospitals.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No hospitals found
                    </td>
                  </tr>
                ) : (
                  filteredHospitals.map((hospital) => (
                    <tr key={hospital._id} className="hover:bg-gray-50" data-testid={`hospital-row-${hospital._id}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                            <Building className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{hospital.name || 'N/A'}</p>
                            <p className="text-sm text-gray-500">{hospital.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1 text-sm text-gray-900">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          {hospital.city || 'N/A'}{hospital.state ? `, ${hospital.state}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-900">{hospital.bed_count || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(hospital.specialties || []).slice(0, 2).map((s, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                          ))}
                          {(hospital.specialties || []).length > 2 && (
                            <Badge variant="outline" className="text-xs">+{hospital.specialties.length - 2}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <Badge className={getStatusBadge(hospital.account_status)}>
                          {hospital.account_status || 'pending'}
                        </Badge>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(hospital)} data-testid={`edit-hospital-${hospital._id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {hospital.account_status !== 'verified' && (
                            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(hospital._id, 'verified')} className="text-green-600">
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {hospital.account_status !== 'suspended' && (
                            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(hospital._id, 'suspended')} className="text-red-600">
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedHospital(hospital); setShowDeleteDialog(true); }} className="text-red-600" data-testid={`delete-hospital-${hospital._id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Hospital Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-hospital-dialog">
          <DialogHeader>
            <DialogTitle>Add New Hospital</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {renderFormContent()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-create-hospital">Create Hospital</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Hospital Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-hospital-dialog">
          <DialogHeader>
            <DialogTitle>Edit Hospital</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {renderFormContent()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-edit-hospital">Update Hospital</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-hospital-dialog">
          <DialogHeader>
            <DialogTitle>Delete Hospital</DialogTitle>
          </DialogHeader>
          <p className="py-4">Are you sure you want to delete <strong>{selectedHospital?.name}</strong>? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-hospital">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
